import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';

const copyToClipboard = (text, label = 'Value') => {
  if (!text) return;
  navigator.clipboard.writeText(text).then(
    () => {
      const el = document.createElement('div');
      el.textContent = `${label} copied!`;
      el.style.cssText = `position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#111827;color:#fff;padding:8px 16px;border-radius:8px;font-size:13px;z-index:9999;box-shadow:0 4px 12px rgba(0,0,0,.2);`;
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 1400);
    },
    () => alert('Could not copy — please copy manually.')
  );
};

// ─── Read-only contact card ───
function ContactDetailModal({ contact, onClose }) {
  if (!contact) return null;
  const isStudent = contact.type === 'student';
  const parents = contact.parent_names || [];
  const linkedStudents = contact.linked_students || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`p-5 ${isStudent ? 'bg-gradient-to-r from-blue-600 to-blue-500' : 'bg-gradient-to-r from-yellow-500 to-amber-500'} text-white`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <i className={`fas ${isStudent ? 'fa-user-graduate' : 'fa-user-friends'} text-2xl`}></i>
              </div>
              <div className="min-w-0">
                <h2 className="text-xl font-bold truncate">{contact.name}</h2>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-[10px] bg-white/25 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide">
                    {isStudent ? 'Student' : 'Parent'}
                  </span>
                  {isStudent && contact.class_name && (
                    <span className="text-[11px] text-white/90">
                      <i className="fas fa-chalkboard mr-1"></i>{contact.class_name}
                    </span>
                  )}
                  {isStudent && contact.campus_name && (
                    <span className="text-[11px] text-white/90">
                      <i className="fas fa-building mr-1"></i>{contact.campus_name}
                    </span>
                  )}
                  {!isStudent && linkedStudents.length > 0 && (
                    <span className="text-[11px] text-white/90">
                      <i className="fas fa-child mr-1"></i>
                      {linkedStudents.length} linked student{linkedStudents.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none p-1 flex-shrink-0">&times;</button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Phone */}
            <div className={`rounded-lg border-2 p-4 ${contact.phone ? 'border-teal-200 bg-teal-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-1">
                <i className={`fas fa-phone ${contact.phone ? 'text-teal-600' : 'text-gray-400'}`}></i>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone</span>
              </div>
              {contact.phone ? (
                <>
                  <p className="text-lg font-bold text-gray-800 break-all">{contact.phone}</p>
                  <div className="flex gap-2 mt-2">
                    <a href={`tel:${contact.phone}`} className="flex-1 bg-teal-600 text-white px-3 py-1.5 rounded text-xs font-semibold hover:bg-teal-700 text-center">
                      <i className="fas fa-phone mr-1"></i> Call Now
                    </a>
                    <button onClick={() => copyToClipboard(contact.phone, 'Phone')} className="px-3 py-1.5 bg-white border border-teal-300 text-teal-700 rounded text-xs font-semibold hover:bg-teal-100">
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                </>
              ) : <p className="text-sm text-gray-400 italic">Not on file</p>}
            </div>

            {/* Email */}
            <div className={`rounded-lg border-2 p-4 ${contact.email ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-1">
                <i className={`fas fa-envelope ${contact.email ? 'text-blue-600' : 'text-gray-400'}`}></i>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</span>
              </div>
              {contact.email ? (
                <>
                  <p className="text-sm font-semibold text-gray-800 break-all">{contact.email}</p>
                  <div className="flex gap-2 mt-2">
                    <a href={`mailto:${contact.email}`} className="flex-1 bg-blue-600 text-white px-3 py-1.5 rounded text-xs font-semibold hover:bg-blue-700 text-center">
                      <i className="fas fa-envelope mr-1"></i> Send Email
                    </a>
                    <button onClick={() => copyToClipboard(contact.email, 'Email')} className="px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded text-xs font-semibold hover:bg-blue-100">
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                </>
              ) : <p className="text-sm text-gray-400 italic">Not on file</p>}
            </div>
          </div>

          {/* Student info */}
          {isStudent && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                <i className="fas fa-id-card mr-1"></i> Student Information
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide">Class</p>
                  <p className="font-semibold text-gray-800">{contact.class_name || '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide">Registration #</p>
                  <p className="font-mono font-semibold text-gray-800">{contact.registration_number || '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide">Roll #</p>
                  <p className="font-semibold text-gray-800">{contact.roll_no || '—'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Guardian (student only) */}
          {isStudent && (contact.guardian_name || contact.guardian_phone || contact.guardian_email) && (
            <div className="rounded-lg border-2 border-orange-200 bg-orange-50 p-4">
              <p className="text-xs font-semibold text-orange-700 uppercase tracking-wide mb-3">
                <i className="fas fa-user-shield mr-1"></i> Guardian Contact
              </p>
              <p className="font-semibold text-gray-800 mb-2">{contact.guardian_name || '—'}</p>
              <div className="flex flex-wrap gap-2">
                {contact.guardian_phone && (
                  <>
                    <a href={`tel:${contact.guardian_phone}`} className="bg-orange-600 text-white px-3 py-1.5 rounded text-xs font-semibold hover:bg-orange-700">
                      <i className="fas fa-phone mr-1"></i> {contact.guardian_phone}
                    </a>
                    <button onClick={() => copyToClipboard(contact.guardian_phone, 'Guardian phone')} className="px-3 py-1.5 bg-white border border-orange-300 text-orange-700 rounded text-xs font-semibold hover:bg-orange-100">
                      <i className="fas fa-copy"></i>
                    </button>
                  </>
                )}
                {contact.guardian_email && (
                  <a href={`mailto:${contact.guardian_email}`} className="bg-white border border-orange-300 text-orange-700 px-3 py-1.5 rounded text-xs font-semibold hover:bg-orange-100">
                    <i className="fas fa-envelope mr-1"></i> {contact.guardian_email}
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Linked Students (parent only) — relationship field removed */}
          {!isStudent && linkedStudents.length > 0 && (
            <div className="rounded-lg border-2 border-green-200 bg-green-50 p-4">
              <p className="text-xs font-semibold text-green-800 uppercase tracking-wide mb-3">
                <i className="fas fa-child mr-1"></i>
                Linked Student{linkedStudents.length !== 1 ? 's' : ''} ({linkedStudents.length})
              </p>
              <div className="space-y-2">
                {linkedStudents.map(child => (
                  <div
                    key={child.student_id}
                    className="bg-white rounded-lg border border-green-200 p-3 flex items-center gap-3"
                  >
                    <div className="w-10 h-10 rounded-full bg-green-100 text-green-700 flex items-center justify-center flex-shrink-0 font-bold">
                      {child.first_name?.[0] || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-gray-800 truncate">
                        {child.name}
                        {child.is_primary && (
                          <span className="ml-2 text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">
                            PRIMARY
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {child.class_name && (
                          <span className="mr-3">
                            <i className="fas fa-chalkboard mr-1"></i>
                            {child.class_name}
                          </span>
                        )}
                        {child.registration_number && (
                          <span className="font-mono">{child.registration_number}</span>
                        )}
                      </p>
                      {/* ⚠️ Relationship field intentionally NOT shown — unreliable in data */}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state for parents with no linked students */}
          {!isStudent && linkedStudents.length === 0 && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
              <i className="fas fa-user-slash text-2xl text-gray-300 mb-2"></i>
              <p className="text-xs text-gray-500 italic">
                No students linked to this parent account yet.
              </p>
            </div>
          )}

          {/* Linked parent accounts (student only) */}
          {isStudent && parents.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                <i className="fas fa-users mr-1"></i> Linked Parent Account{parents.length !== 1 ? 's' : ''}
              </p>
              <div className="space-y-2">
                {parents.map((name, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="w-8 h-8 rounded-full bg-yellow-100 text-yellow-700 flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-user text-xs"></i>
                    </span>
                    <span className="font-medium text-gray-800">{name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CNIC */}
          {contact.cnic && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                <i className="fas fa-id-card mr-1"></i> CNIC / ID Number
              </p>
              <div className="flex items-center gap-2">
                <p className="font-mono font-semibold text-gray-800">{contact.cnic}</p>
                <button onClick={() => copyToClipboard(contact.cnic, 'CNIC')} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                  <i className="fas fa-copy"></i>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Contacts Directory — MAIN (read-only)
// ═══════════════════════════════════════════════════════════
export default function ContactsDirectory() {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  const loadContacts = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/reception/contacts');
      setContacts(res.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadContacts(); }, []);

  const availableClasses = useMemo(() => {
    const set = new Set();
    contacts.filter(c => c.type === 'student' && c.class_name).forEach(c => set.add(c.class_name));
    return [...set].sort((a, b) => {
      const pa = a.match(/^(\d+)/);
      const pb = b.match(/^(\d+)/);
      if (pa && pb) {
        const n = parseInt(pa[1]) - parseInt(pb[1]);
        if (n !== 0) return n;
      }
      return a.localeCompare(b);
    });
  }, [contacts]);

  const filtered = useMemo(() => {
    let list = contacts;
    if (typeFilter !== 'all') list = list.filter(c => c.type === typeFilter);
    if (classFilter !== 'all') list = list.filter(c => c.type === 'student' && c.class_name === classFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c => {
        const blob = [
          c.name, c.phone, c.email, c.cnic, c.registration_number,
          c.class_name, c.roll_no, c.guardian_name, c.guardian_phone,
          c.guardian_email,
          ...(c.parent_names || []),
          ...((c.linked_students || []).flatMap(s => [
            s.name, s.class_name, s.registration_number,
          ])),
        ].filter(Boolean).join(' ').toLowerCase();
        return blob.includes(q);
      });
    }
    return list;
  }, [contacts, typeFilter, classFilter, search]);

  const linkedCount = (c) => (c.linked_students || []).length;

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <i className="fas fa-address-book text-teal-500"></i>
            Contacts Directory
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Search any student or parent to get their contact details.
          </p>
        </div>
        <button
          onClick={loadContacts}
          disabled={loading}
          className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
        >
          <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'} mr-1`}></i> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-3 rounded shadow mb-4 flex flex-wrap gap-3 items-center">
        <select className="p-2 border rounded text-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="all">All Contacts</option>
          <option value="student">Students</option>
          <option value="parent">Parents</option>
        </select>

        <select
          className="p-2 border rounded text-sm"
          value={classFilter}
          onChange={e => setClassFilter(e.target.value)}
          disabled={typeFilter === 'parent'}
        >
          <option value="all">All Classes</option>
          {availableClasses.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <input
          type="text"
          placeholder="Search by name, phone, email, CNIC, reg#..."
          className="flex-1 min-w-[200px] p-2 border rounded text-sm"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        <span className="text-sm text-gray-500">
          {filtered.length} contact{filtered.length !== 1 ? 's' : ''}
        </span>

        {(search || typeFilter !== 'all' || classFilter !== 'all') && (
          <button
            onClick={() => { setSearch(''); setTypeFilter('all'); setClassFilter('all'); }}
            className="text-xs text-blue-600 hover:underline font-medium"
          >
            Clear filters
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4 text-sm">
          <i className="fas fa-exclamation-triangle mr-1"></i> <strong>Error:</strong> {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white p-4 rounded shadow">
        {loading ? (
          <p className="text-gray-500 text-center py-8">Loading contacts…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <i className="fas fa-address-book text-4xl mb-3"></i>
            <p>{contacts.length === 0 ? 'No contacts available.' : 'No contacts match your search.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Type', 'Name', 'Class / Reg #', 'Phone', 'Email', 'Guardian / Parents / Children', ''].map(h => (
                    <th key={h} className="p-3 text-left font-semibold text-gray-700 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr
                    key={`${c.type}-${c.id}`}
                    className="border-t hover:bg-teal-50 cursor-pointer"
                    onClick={() => setSelected(c)}
                  >
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                        c.type === 'student' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {c.type === 'student' ? 'Student' : 'Parent'}
                      </span>
                    </td>
                    <td className="p-3 font-medium">{c.name}</td>
                    <td className="p-3 text-xs text-gray-600">
                      {c.class_name || '—'}
                      {c.registration_number && <span className="block font-mono">{c.registration_number}</span>}
                    </td>
                    <td className="p-3">
                      {c.phone ? (
                        <a href={`tel:${c.phone}`} onClick={e => e.stopPropagation()} className="text-teal-600 hover:underline whitespace-nowrap">
                          <i className="fas fa-phone mr-1"></i>{c.phone}
                        </a>
                      ) : '—'}
                    </td>
                    <td className="p-3 text-xs">
                      {c.email ? (
                        <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()} className="text-blue-600 hover:underline truncate block max-w-[200px]">
                          {c.email}
                        </a>
                      ) : '—'}
                    </td>
                    <td className="p-3 text-xs">
                      {c.type === 'student' && (
                        <>
                          {c.guardian_name && (
                            <div className="text-gray-700">
                              <i className="fas fa-user-shield text-orange-500 mr-1"></i>
                              {c.guardian_name}
                              {c.guardian_phone && (
                                <a href={`tel:${c.guardian_phone}`} onClick={e => e.stopPropagation()} className="ml-1 text-teal-600 hover:underline">
                                  ({c.guardian_phone})
                                </a>
                              )}
                            </div>
                          )}
                          {c.parent_names?.length > 0 && (
                            <div className="text-gray-500 mt-0.5">P: {c.parent_names.join(', ')}</div>
                          )}
                          {!c.guardian_name && !c.parent_names?.length && '—'}
                        </>
                      )}

                      {c.type === 'parent' && (
                        linkedCount(c) > 0 ? (
                          <div className="text-gray-700">
                            <i className="fas fa-child text-green-600 mr-1"></i>
                            <span className="font-medium">{linkedCount(c)} student{linkedCount(c) !== 1 ? 's' : ''}:</span>
                            <span className="ml-1 text-gray-600">
                              {(c.linked_students || []).map(s => s.name).join(', ')}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">No students linked</span>
                        )
                      )}
                    </td>
                    <td className="p-3">
                      <span className="px-3 py-1 text-xs bg-teal-100 text-teal-800 rounded font-semibold whitespace-nowrap">
                        <i className="fas fa-address-card mr-1"></i> View
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && <ContactDetailModal contact={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}